import React, { useState } from 'react'

import Box from '@ilg-totallymoney/ui/components/Box'
import Grid from '@ilg-totallymoney/ui/components/Grid'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import LinkButton from '@ilg-totallymoney/ui/components/LinkButton'
import LinkWithChevron from '@ilg-totallymoney/ui/components/LinkWithChevron'
import WaveDivider from '@ilg-totallymoney/ui/components/WaveDivider'

import { ScoredCreditCardProduct } from '@appTypes/EligibilityApi.interface'

import CardImage from '@components/CardImage'
import CardPageProductLists from '@components/CardPageProductLists'
import CreditCardContent from '@components/CreditCardContent'
import RatesAndFees from '@components/RatesAndFees'
import RepresentativeExample from '@components/RepresentativeExample'
import RepresentativeExampleModal from '@components/RepresentativeExampleModal'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'

import { borderStyle } from '@styles/borderStyle'

import { getIsDormantScoreIdJourney } from '@utils/getIsDormantScoreIdJourney'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'
import { getRedirectAccountUrl } from '@utils/getRedirectAccountUrl'
import { getSanitisedCardName } from '@utils/getSanitisedCardName'
import { getUnscoredCategoryPath } from '@utils/getUnscoredCategoryPath'

import * as Layout from './components/UnscoredCardLayout'
import { getLoginUrl } from './utils/getLoginUrl'

interface UnscoredCardProps {
    unscoredCard: ScoredCreditCardProduct
}

enum ModalType {
    RepresentativeExample = 'RepresentativeExample',
}

const UnscoredCard = ({ unscoredCard }: UnscoredCardProps) => {
    const { logCheckEligibilityEvent } = useLogEvents()

    const {
        appQueryState,
        getQueryStringWithAppState,
        getRouteUrl,
        goToRouteWithAppState,
    } = useAppQueryState()

    const [showModal, setShowModal] = useState<ModalType | null>(null)

    const formHref =
        appQueryState.mediaSource === 'VTSRTG'
            ? getLoginUrl()
            : getRedirectAccountUrl({
                  queryObject: {
                      category:
                          appQueryState.category === 'all-cards'
                              ? ''
                              : appQueryState.category,
                      card: getSanitisedCardName(unscoredCard.name),
                      ccid: unscoredCard.productCode,
                  },
                  queryString: getQueryStringWithAppState(),
              })

    const onCheckEligibilityClick = (
        event: React.SyntheticEvent<Element, Event>
    ) => {
        event.preventDefault()

        if (getIsDormantScoreIdJourney(appQueryState)) {
            return goToRouteWithAppState(appQueryState.category!, {
                queryParameters: {
                    scoreId: appQueryState.dormantScoreId,
                    customerId: appQueryState.customerId,
                },
            })
        }

        logCheckEligibilityEvent({
            trigger: 'row',
            category: appQueryState.categoryId!,
            productCode: unscoredCard.productCode,
            rowNumber: 0,
        })

        window.location.href = formHref
    }

    const categoryPath = getUnscoredCategoryPath(appQueryState.categoryId!)

    return (
        <>
            <Grid>
                <Layout.BackLink>
                    <LinkWithChevron
                        href={getRouteUrl(categoryPath)}
                        variant="100"
                        direction="left"
                        onClick={event => {
                            event.preventDefault()
                            goToRouteWithAppState(categoryPath)
                        }}
                    >
                        Back to cards
                    </LinkWithChevron>
                </Layout.BackLink>
                <Layout.PageHeading>
                    <Heading as="h1" variant="60" color="light">
                        {unscoredCard.name}
                    </Heading>
                </Layout.PageHeading>
                <Box
                    gridColumn="1/13"
                    display="grid"
                    gridGap={['space30', 'space50']}
                    pb={['space30', 'space90']}
                >
                    <Layout.ItemDetails>
                        <Layout.ItemImage>
                            <Box width={[200, 139, 180]}>
                                <CardImage
                                    name={unscoredCard.name}
                                    cmsImageUrl={unscoredCard.imageUrlLarge}
                                    isPortraitImage={getIsProductPortraitImage(
                                        unscoredCard.productCode
                                    )}
                                />
                            </Box>
                        </Layout.ItemImage>
                        <Layout.ItemContent>
                            <Box pb="space30">
                                <CreditCardContent
                                    category={appQueryState.categoryId!}
                                    creditCard={unscoredCard}
                                />
                            </Box>
                            <RepresentativeExample
                                item={unscoredCard}
                                onTooltipPress={() =>
                                    setShowModal(
                                        ModalType.RepresentativeExample
                                    )
                                }
                            />
                        </Layout.ItemContent>
                    </Layout.ItemDetails>
                    <Layout.ItemButtons>
                        <Box
                            position={['fixed', 'relative']}
                            bottom={0}
                            left={0}
                            zIndex={2}
                            width={['100%', '350px', 'auto']}
                            backgroundColor="white"
                            borderTop={borderStyle}
                        >
                            <Box p={['space30', 0]}>
                                <LinkButton
                                    href={formHref}
                                    variant="primary"
                                    text="Check my eligibility"
                                    onClick={onCheckEligibilityClick}
                                    data-gtm-event="eligibility-table-cardSpecific"
                                    data-testid="eligibility-table-cardSpecific"
                                />
                            </Box>
                        </Box>
                    </Layout.ItemButtons>
                </Box>
            </Grid>

            <Box
                backgroundColor="indigo10"
                position="relative"
                py={['space30', 'space40']}
            >
                <Box
                    position="absolute"
                    top={['-space30', '-space30', '-space40']}
                    zIndex={1}
                    width="100%"
                >
                    <WaveDivider waveColor="#f9f8fe" />
                </Box>

                <Grid>
                    <Box gridColumn="1/13">
                        <CardPageProductLists card={unscoredCard} />
                    </Box>
                </Grid>
            </Box>

            <Box backgroundColor="white" position="relative">
                <Box
                    position="absolute"
                    top={['-space30', '-space30', '-space40']}
                    zIndex={1}
                    width="100%"
                >
                    <WaveDivider waveColor="white" />
                </Box>

                <Grid>
                    <Box
                        as="section"
                        gridColumn="1/13"
                        pb="space50"
                        py={['space30', 'space40']}
                    >
                        <Box mb="space20">
                            <Heading variant="60">Rates and Fees</Heading>
                        </Box>

                        <Box>
                            <RatesAndFees card={unscoredCard} />
                        </Box>
                    </Box>
                </Grid>
            </Box>
            <RepresentativeExampleModal
                showModal={showModal == ModalType.RepresentativeExample}
                onModalClose={() => setShowModal(null)}
            />
        </>
    )
}

export default UnscoredCard
